import React from 'react';

import CoverSheet from '../general/coverSheet';
import ETAnomalyPDF from '../et/ETAnomalyPDF';
import PDFInfoSheet from '../../../themed/pdf/pdfInfoSheet';
import PDFPage from '../../../themed/pdf/pdfPage';
import PDFPhotoPage from '../../../themed/pdf/pdfPhotoPage';
import UTMeasurementsOverview from './utMeasurementsOverview';

import withPDF from '../../../hoc/withPDF';

import * as models from '../../../../utils/models';
import * as utils from '../../../../utils/utilities';

interface MUTReportProps {
	anomalyData: models.VTEquipmentAnomalyData[];
	projectDetails: models.Project;
	coverSheetData: models.MUTCoverData;
	isoImage: models.PDFImageProps[];
	inspectionItems: models.MUTUTInspectionItem[];
	inspectionOverview: models.MUTUTInspectionOverviewData;
	photographicData: models.PDFImageProps[];
}

const MUTReport: React.FC<MUTReportProps> = (props): JSX.Element => {
	const { inspectionItems, photographicData, projectDetails } = props;
	const { documentNumber, pidImages } = projectDetails;

	const standardCriteriaItems: models.CoverDataItem[] = [
		{
			label: '1. Qualification of NDT Personnel',
			value: props.coverSheetData?.ndtQualification,
		},
		{
			label: '2. Non Destructive Testing',
			value: props.coverSheetData?.nonDestructiveTesting,
		},
		{
			label: '3.  Pressure Vessel Inspection',
			value: props.coverSheetData?.pressureVesselInspection,
		},
		{
			label: '4.  Process Piping Inspection Code',
			value: props.coverSheetData?.pipingInspectionCode,
		},
		{
			label: '5.  Process Piping Design',
			value: props.coverSheetData?.pipingDesign,
		},
		{
			label: '6.  Pressure Vessel Design',
			value: props.coverSheetData?.pressureVesselDesign,
		},
		{
			label: '7.  Inspection of Piping System Component',
			value: props.coverSheetData?.pipingSystemComponent,
		},
		{
			label:
				'8.  Damage Mechanisms Affecting Fixed Equipment in the Refining Industry',
			value: props.coverSheetData?.damageMechanisms,
		},
	];

	const referenceDocuments: models.CoverDataItem[] = [
		{
			label: '1. Scope Of Work',
			value: props.coverSheetData?.scopeOfWork,
		},
		{
			label: '2. VT Procedure',
			value: props.coverSheetData?.vtProcedure,
		},
		{
			label: '3. Rope Access Code of Practice',
			value: props.coverSheetData?.codeOfPractice,
		},
		{
			label: `4. ${props.projectDetails.organisationName} Work procedure`,
			value: props.coverSheetData?.workProcedure,
		},
	];

	const pageSubtitle: string = 'Document Number: ' + documentNumber;
	const splitData = utils.getChunkedData(inspectionItems, 15);
	const splitImages = utils.getChunkedData(photographicData, 4);

	return (
		<React.Fragment>
			<PDFInfoSheet
				companyUrl={props.projectDetails.organisation.organisationLogo}
				documentNo={props.projectDetails.documentNumber}
				clientName={props.projectDetails.organisationName}
				facility={props.projectDetails.facilityName}
				reportTitle={props.projectDetails.projectTitle}
			/>

			<PDFPage
				pageDetails={utils.getPageDetails('Cover Sheet', pageSubtitle)}
				projectDetails={props.projectDetails}
			>
				<CoverSheet
					overviewData={overviewData}
					referenceDocuments={referenceDocuments}
					standardCriteriaItems={standardCriteriaItems}
					note="Note: Spot UT-inspection is carried out to confirm the integrity of
          pipe under heavy scale as per client requirement"
				/>
			</PDFPage>

			<PDFPage
				projectDetails={props.projectDetails}
				pageDetails={utils.getPageDetails('P & ID')}
			>
				<PDFPhotoPage photos={utils.getPIDImages(pidImages)} />
			</PDFPage>

			<PDFPage
				projectDetails={props.projectDetails}
				pageDetails={utils.getPageDetails('ISO Drawing')}
			>
				<PDFPhotoPage photos={props.isoImage} />
			</PDFPage>

			{splitData.map((data: models.MUTUTInspectionItem[], index: number) => (
				<PDFPage
					key={index}
					projectDetails={props.projectDetails}
					pageDetails={utils.getPageDetails('UT Measurements')}
					enableTitlePage={index === 0}
				>
					<UTMeasurementsOverview
						projectDetails={props.projectDetails}
						data={data}
						{...props.inspectionOverview}
					/>
				</PDFPage>
			))}

			<ETAnomalyPDF data={props.anomalyData} project={projectDetails} />

			{splitImages.map((images, index) => (
				<PDFPage
					key={index}
					projectDetails={props.projectDetails}
					pageDetails={utils.getPageDetails(
						'Photographic Record - ' + (index + 1)
					)}
					enableTitlePage={index === 0}
				>
					<PDFPhotoPage photos={images} />
				</PDFPage>
			))}
		</React.Fragment>
	);
};

const overviewData: models.CoverDataOverview[] = [
	{ report: '1. Cover Sheet', required: 'Yes', page: '2' },
	{ report: '2. P & ID', required: 'Yes', page: '4' },
	{
		report: '3. ISO Page',
		required: 'Yes',
		page: '6',
	},
	{
		report: '4. UT Measurements',
		required: 'Yes',
		page: '8',
	},
	{
		report: '5. Anomalies',
		required: 'Yes',
		page: '10',
	},
	{
		report: '6. Photographic Report',
		required: 'Yes',
		page: '12',
	},
];

export default withPDF(MUTReport);
